import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterField } from '@/components/FilterButton'
import { StatusFilterButton, type StatusFilterValue } from '@/components/StatusFilter'
import type { BiddingFilter } from '@/services/advertisers/advertisersApi'

export interface AdvertiserFilterState {
  objectState: StatusFilterValue
  /** Whether the advertiser has bid in the last 24 hours. */
  bidding: BiddingFilter
}

export const DEFAULT_ADVERTISER_FILTERS: AdvertiserFilterState = {
  objectState: 'ACTIVE',
  bidding: 'all',
}

const BIDDING_OPTIONS: BiddingFilter[] = ['all', 'bidding', 'idle']

interface AdvertiserFiltersProps {
  filters: AdvertiserFilterState
  onChange: (filters: AdvertiserFilterState) => void
  /** What this list treats as unfiltered. */
  defaults?: AdvertiserFilterState
}

/** Status and bidding-activity filters for an advertiser list, in one button. */
export function AdvertiserFilters({
  filters,
  onChange,
  defaults = DEFAULT_ADVERTISER_FILTERS,
}: AdvertiserFiltersProps) {
  const { t } = useTranslation()

  const label = (value: BiddingFilter) =>
    value === 'all' ? t('common.all') : t(`advertisers.${value}`)

  return (
    <StatusFilterButton
      value={filters.objectState}
      onChange={(objectState) => onChange({ ...filters, objectState })}
      defaultValue={defaults.objectState}
      extraActiveCount={filters.bidding !== defaults.bidding ? 1 : 0}
      onReset={() => onChange(defaults)}
    >
      <FilterField label={t('advertisers.biddingActivity')}>
        <Select
          value={filters.bidding}
          onValueChange={(v) => onChange({ ...filters, bidding: v as BiddingFilter })}
        >
          <SelectTrigger className="h-8 w-full text-sm">
            <SelectValue>{label(filters.bidding)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {BIDDING_OPTIONS.map((value) => (
              <SelectItem key={value} value={value}>
                {label(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>
    </StatusFilterButton>
  )
}
